import React, { useState } from "react";
import { IconFrame } from "../IconFrame";
import { UpgradeModal } from "./UpgradeModal";
import { RfModal } from "../RfModal/RfModal";
import { twClassNames } from "../../helpers/twClassNames";
import { useStore } from "zustand";
import globalStore from "../../../../state";
import { useTrial } from "../../hooks/useTrial";
import { Feature, isTrialFeature } from "@functions/featureAvailability";
import { trialDurationHyphenated, trialDurationLabel } from "@functions/plans/trialConstants";
import { getTitleForPlan } from "@functions/plans/utilities";
import { Plan } from "@functions/plans/types";
import { RankedCTAGroup, buildUpsellCTAActions } from "../Trial/TrialStartBanner";

interface AvailablePlan {
    plan: string;
    description: string;
}

interface GatedFeatureModalProps {
    icon: string;
    featureId?: Feature;
    feature: string;
    description: string;
    learnMoreLink: string;
    valueProps: string[];
    availablePlans?: AvailablePlan[];
    onlyOnePlan?: boolean;
    singlePlan?: string;
    enterpriseFeature?: boolean;
    isOpen?: boolean;
    onClose?: () => void;
    className?: string;
}

const ValueProp: React.FC<{ text: string }> = ({ text }) => {
    return (
        <div className="flex items-center gap-2 text-sm font-normal text-secondary">
            <i className="fa-regular fa-check text-purple-600"></i>
            {text}
        </div>
    );
};

const AvailablePlan: React.FC<{ availablePlan: AvailablePlan }> = ({ availablePlan }) => {
    return (
        <div className="flex items-center text-sm font-normal text-secondary">
            <div className="flex w-full items-center justify-between gap-2">
                <div className="flex flex-shrink-0 items-center gap-2 text-sm font-semibold">
                    <i className="fa-regular fa-check text-purple-600"></i>
                    {availablePlan?.plan}
                </div>
            </div>
        </div>
    );
};

const GatedFeatureModal: React.FC<GatedFeatureModalProps> = ({
    icon,
    featureId,
    feature,
    description,
    learnMoreLink,
    valueProps,
    availablePlans,
    onlyOnePlan = false,
    singlePlan = "",
    enterpriseFeature = false,
    isOpen,
    onClose,
    className
}) => {
    const [isUpgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const { usage, currentWorkspaceId } = useStore(globalStore, (state) => ({
        usage: state.getUsage(),
        currentWorkspaceId: state.currentWorkspaceId
    }));
    const { isEligibleForTrial, activateTrial, isActivating: isActivatingTrial } = useTrial();
    const featureAvailableOnTrial = featureId ? isTrialFeature(featureId) : false;
    const showTrialCTA = isEligibleForTrial && featureAvailableOnTrial;
    const showUpgradeCTA = !enterpriseFeature;
    const trialPlanName = getTitleForPlan(Plan.UbpPremiumTrial);
    const trialButtonLabel = enterpriseFeature
        ? "Try it with a trial"
        : `Start my ${trialDurationHyphenated} trial`;
    const activateTrialWithOptions = () =>
        activateTrial({
            funnelSource: `upgrade-${feature.toLowerCase().replace(/\s+/g, "-")}`,
            feature: featureId,
            onSuccess: onClose
        });

    const handleSalesFormClick = () => {
        (window as any)?.open("https://roboflow.com/sales", "_blank");
        (window as any)?.eventTracker?.event({
            type: "Upsell Sales Form Clicked",
            data: {
                funnelSource: `upgrade-${feature.toLowerCase().replace(/\s+/g, "-")}`,
                clickedFrom: "gated_feature_modal",
                workspaceId: currentWorkspaceId,
                currentPlan: usage?.plan
            }
        });
    };

    const openUpgradeModal = () => {
        (window as any)?.eventTracker?.event({
            type: "Upsell Upgrade CTA Clicked",
            data: {
                funnelSource: `upgrade-${feature.toLowerCase().replace(/\s+/g, "-")}`,
                clickedFrom: "gated_feature_modal",
                workspaceId: currentWorkspaceId,
                currentPlan: usage?.plan
            }
        });
        setUpgradeModalOpen(true);
    };

    const ctaActions = buildUpsellCTAActions({
        showTrialCTA,
        showUpgradeCTA,
        trialButtonLabel,
        isActivatingTrial,
        activateTrial: activateTrialWithOptions,
        openUpgradeOrPricing: openUpgradeModal,
        handleSalesFormClick
    });

    const isAModal = isOpen !== undefined && onClose !== undefined;

    const content = (
        <div
            className={twClassNames(
                "flex max-w-[498px] flex-col items-center gap-4 rounded-lg bg-white text-center text-sm font-normal text-secondary",
                isAModal ? "border-none" : "border border-gray-300 p-6 shadow-sm"
            )}
        >
            <div className={`${icon && "mt-2"} modalHeader flex flex-col gap-3`}>
                {icon && (
                    <div className="flex justify-center">
                        <IconFrame icon={icon} size="lg" isCircle={true} color="yellow" />
                    </div>
                )}
                <span className="modalTitle w-full text-lg font-semibold text-gray-900">
                    Upgrade to Unlock {feature}
                </span>
            </div>
            <p>
                <i className="far fa-info-circle mr-1 text-gray-400"></i> {description}
                {learnMoreLink && (
                    <span>
                        <a href={learnMoreLink}> Learn More</a>
                    </span>
                )}
            </p>
            {valueProps.length > 0 && (
                <div className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    {valueProps.map((valueProp) => (
                        <ValueProp text={valueProp} key={valueProp} />
                    ))}
                </div>
            )}
            {showTrialCTA ? (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-center text-sm text-yellow-900">
                    {enterpriseFeature
                        ? `${feature} is an Enterprise feature. Try it for free with a trial.`
                        : `${feature} is available on the ${trialPlanName}. Try it free for ${trialDurationLabel}.`}
                </div>
            ) : enterpriseFeature ? (
                <div className="text-gray-600">
                    Talk to our sales team to enable{" "}
                    <span className="font-semibold">{feature}</span> for your workspace.
                </div>
            ) : onlyOnePlan ? (
                <div>
                    {feature} is an <span className="font-semibold">{singlePlan}-only</span>{" "}
                    feature.
                </div>
            ) : (
                <>
                    {feature} is available on:
                    <div className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        {availablePlans?.map((availablePlan) => (
                            <AvailablePlan key={availablePlan.plan} availablePlan={availablePlan} />
                        ))}
                    </div>
                </>
            )}

            <RankedCTAGroup actions={ctaActions} />
            {isUpgradeModalOpen && (
                <UpgradeModal
                    isModalOpen={isUpgradeModalOpen}
                    closeModal={() => setUpgradeModalOpen(false)}
                    excludePublic={false}
                    fsrc="gated-feature-modal"
                    className="z-[4000]"
                />
            )}
        </div>
    );

    if (isAModal) {
        return (
            <RfModal isOpen={isOpen} onClose={onClose} className={className}>
                <RfModal.Overlay />
                <RfModal.Panel className="max-w-[498px]">
                    <RfModal.CloseButton />
                    {content}
                </RfModal.Panel>
            </RfModal>
        );
    }

    return content;
};

export default GatedFeatureModal;
